import React from "react";
import Markdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import {
	Box, Button,
	Link,
	ListItem,
	OrderedList,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text, Th,
	Thead,
	Tr,
	UnorderedList
} from "~/foundation/Chakra";
import { Icon } from "../Icon";
import { useTranslation } from "~/foundation/Dictionary";
import type { SearchDictionary } from "~/feature/Search/dictionary.ts"; //eslint-disable-line
import type { AliaChatQuestion } from "~/foundation/SitecoreContent/generated-types.ts";

type ChatMarkdownProps = {
	children: string;
	formatMessageQuestions?: boolean;
	handleQuestion?: (question: AliaChatQuestion) => void;
	chatLogId?: string;
};

function ChatMarkdown(props: ChatMarkdownProps) {
	const [t] = useTranslation<SearchDictionary>();
	const markDownProps = props;

	return <Markdown remarkPlugins={[remarkGfm]} components={{
		p(props) {
			return <Text my={5}>{props.children}</Text>
		},
		a(props) {
			return <Link color="primary.aramcoLinkBlue" {...props}>{props.children}</Link>
		},
		ol(props) {
			return <OrderedList my={5}>{props.children}</OrderedList>
		},
		ul(props) {
			return <UnorderedList my={5}>{props.children}</UnorderedList>
		},
		li(props) {
			const question = props.children?.toString().trim();
			const endsWithQuestionMark = question?.endsWith('?');
			const addAliaButton = markDownProps.formatMessageQuestions && endsWithQuestionMark && question && typeof markDownProps.handleQuestion === "function";

			if (addAliaButton){
				return (
					<ListItem my={4} ms="-1rem" listStyleType="none" display="grid" gridTemplateColumns="auto max-content" gap={4}>
						<Box>{question}</Box>
						<Button
							variant="outline"
							size="md"
							color="primary.darkBlue"
							borderColor="currentcolor"
							h="fit-content"
							alignSelf="center"
							fontSize=".75rem"
							lineHeight=".75rem"
							p="0 .25rem 0 .5rem"
							onClick={() => {
								if (markDownProps.handleQuestion && markDownProps.chatLogId){
									markDownProps.handleQuestion({ question: question, chatlogId: markDownProps.chatLogId })
								}
							}}>
							<span>{t("search.askAlia")}</span>
							<Icon
								color="currentColor"
								variant="Alia"
								ms=".5rem"
								w="1.5rem"
								position="relative"
								zIndex={1}
							/>
						</Button>
					</ListItem>
				)
			} else {
				return <ListItem my={2}>{props.children}</ListItem>
			}

		},
		table(props) {
			return <TableContainer my={5}><Table variant="striped" size="sm" my={2} minW="full">{props.children}</Table></TableContainer>
		},
		thead(props) {
			return <Thead borderBottom="1px solid" borderColor="primary.lightGrey">{props.children}</Thead>
		},
		tbody(props) {
			return <Tbody>{props.children}</Tbody>
		},
		th(props) {
			return <Th px={1} pb={1} textAlign="start">{props.children}</Th>
		},
		tr(props) {
			return <Tr _notLast={{ borderBottom: "1px solid", borderColor: "primary.lightGrey" }}>{props.children}</Tr>
		},
		td(props) {
			return <Td p={1} whiteSpace="pre-wrap">{props.children}</Td>
		}
	}}>
		{props.children}
	</Markdown>
}

export default ChatMarkdown;